import { FontIcon } from '@brandfolder/react';
import { Trans } from '@lingui/macro';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { DragSource } from 'react-dnd';

import Checkbox from '@components/common/checkbox/main';
import dragDropTypes from '@components/common/drop_zone/drag_drop_types';
import DropZone from '@components/common/drop_zone/drop_zone';

const SearchPinsRow = (props) => {
  const [isEditing, setIsEditing] = useState(false);
  const [pinLabel, setPinLabel] = useState('');
  const [pinQuery, setPinQuery] = useState('');

  const thisRow = useRef();
  const dragHandleRef = useRef(null);

  const {
    dispatch,
    dragInProgress,
    editingActive,
    searchPin,
    setEditingActive,
    updatedFiltersData,
  } = props; // parent props
  const {
    isDragging,
    isHovered,
    connectDragSource,
  } = props; // React DnD props

  const searchPinIndex = updatedFiltersData.indexOf(searchPin);

  const resetRowData = () => {
    setPinLabel(searchPin.label);
    setPinQuery(searchPin.query);
  };

  useEffect(() => {
    resetRowData();
  }, [searchPin]);

  const generateUpdatedSearchPin = () => {
    const { featured, key, position } = searchPin;

    return { featured, key, label: pinLabel, position, query: pinQuery };
  };

  const toggleEdit = (action) => {
    const updateEditing = (bool) => {
      setIsEditing(bool);
      setEditingActive(bool);
    };

    if (action === 'edit') {
      updateEditing(true);
    }

    if (action === 'close') {
      resetRowData();
      updateEditing(false);
    }

    if (action === 'done') {
      dispatch({ data: generateUpdatedSearchPin(), index: searchPinIndex, type: 'done-editing' });
      updateEditing(false);
    }
  };

  const handleReposition = ({
    draggedIndex,
    hoveredItem: hoveredRow,
  }) => {
    dispatch({
      data: { hoveredRow },
      index: draggedIndex,
      type: 'update-row-position',
    });
  };

  const handleReorderKeyDown = (e) => {
    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      return;
    }
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;

    const total = updatedFiltersData.length;
    if (e.key === 'ArrowUp' && searchPinIndex > 0) {
      e.preventDefault();
      handleReposition({
        draggedIndex: searchPinIndex,
        hoveredItem: { key: updatedFiltersData[searchPinIndex - 1].key, hoveredHalf: 'top' },
      });
      requestAnimationFrame(() => { dragHandleRef.current?.focus(); });
    } else if (e.key === 'ArrowDown' && searchPinIndex < total - 1) {
      e.preventDefault();
      handleReposition({
        draggedIndex: searchPinIndex,
        hoveredItem: { key: updatedFiltersData[searchPinIndex + 1].key, hoveredHalf: 'bottom' },
      });
      requestAnimationFrame(() => { dragHandleRef.current?.focus(); });
    }
  };

  const pinRowInputId = `pin-row-${searchPin.key}`;

  const pinRowContainer = (
    <tr
      ref={thisRow}
      className={classnames(
        "pin-row-container",
        {
          "disable-row": editingActive && !isEditing,
          "disable-row-editing": !isEditing,
          "is-dragging": isDragging,
          "is-hovered": isHovered,
        },
      )}
      id={searchPin.key}
    >
      <td className="pin-row-container__pin">
        <div className="pin-cell-content">
          <button
            ref={dragHandleRef}
            type="button"
            className="row-handle pin-row-container__drag-icon"
            aria-label={`Reorder row: ${pinLabel}. Use arrow keys to move up or down.`}
            aria-describedby={pinRowInputId}
            data-drag-handle
            onKeyDown={handleReorderKeyDown}
          >
            <FontIcon aria-label={`Drag to reorder ${pinLabel || searchPin.label} search pin`} tabIndex={0} icon="drag-indicator" aria-hidden />
          </button>
          <input
            id={pinRowInputId}
            className="brandfolder-input label-input"
            disabled={!isEditing}
            onChange={(e) => setPinLabel(e.target.value)}
            value={pinLabel}
            aria-label="Pin name"
          />
        </div>
        {!editingActive && dragInProgress && (
          <>
            <DropZone
              dragDropType="SEARCH_PIN"
              dropHalf="top"
              handleReposition={handleReposition}
              hoveredItemKey={searchPin.key}
            />
            <DropZone
              dragDropType="SEARCH_PIN"
              dropHalf="bottom"
              handleReposition={handleReposition}
              hoveredItemKey={searchPin.key}
            />
          </>
        )}
      </td>
      <td className="pin-row-container__featured">
        <Checkbox
          ariaLabel={`Featured: ${pinLabel}`}
          checked={searchPin.featured}
          size="md"
          toggle={() => dispatch({ type: 'update-featured', index: searchPinIndex })}
        />
      </td>
      <td className="pin-row-container__query">
        <input
          className="brandfolder-input query-input"
          disabled={!isEditing}
          onChange={(e) => setPinQuery(e.target.value)}
          value={pinQuery}
          aria-label={`Query for ${pinLabel}`}
        />
      </td>
      <td className="pin-row-container__buttons">
        {isEditing ? (
          <>
            <button
              className="button tertiary sm cancel-edit-button"
              onClick={() => toggleEdit("close")}
              type="button"
            >
              <Trans>Cancel</Trans>
            </button>
            <button
              className="button primary sm done-editing-button"
              onClick={() => toggleEdit("done")}
              type="button"
            >
              <Trans>Update</Trans>
            </button>
          </>
        ) : (
          <button
            className="button tertiary sm edit-row-button"
            onClick={() => toggleEdit("edit")}
            type="button"
            aria-label={`Edit ${pinLabel}`}
          >
            <span className="bff-edit" aria-hidden />
            <Trans>Edit</Trans>
          </button>
        )}
      </td>
      <td className="pin-row-container__buttons">
        {!isEditing && (
          <button
            aria-label={`Delete ${pinLabel || searchPin.label} search pin`}
            className="delete-row-button"
            onClick={() => dispatch({ index: searchPinIndex, type: 'delete-row' })}
            type="button"
          >
            <span className="bff-trash" aria-hidden />
          </button>
        )}
      </td>
    </tr>
  );

  // disable drag n drop when editing row
  if (editingActive) {
    return pinRowContainer;
  }

  return connectDragSource(pinRowContainer);
};

SearchPinsRow.propTypes = {
  dispatch: PropTypes.func.isRequired,
  dragInProgress: PropTypes.bool.isRequired,
  editingActive: PropTypes.bool.isRequired,
  searchPin: PropTypes.shape({}).isRequired,
  setEditingActive: PropTypes.func.isRequired,
  updatedFiltersData: PropTypes.arrayOf(PropTypes.shape({})).isRequired,

  // props used for React DnD wrapper
  /* eslint-disable react/no-unused-prop-types */
  index: PropTypes.number.isRequired,
  setDragInProgress: PropTypes.func.isRequired,
  /* eslint-enable react/no-unused-prop-types */
};

const dragSource = {
  beginDrag(props) {
    props.setDragInProgress(true);

    return { key: props.searchPin.key, draggedIndex: props.index };
  },
  endDrag(props) {
    props.setDragInProgress(false);
  }
};

const dragCollect = (connect, monitor) => ({
  connectDragSource: connect.dragSource(),
  isDragging: monitor.isDragging(),
});

export default DragSource(dragDropTypes.SEARCH_PIN, dragSource, dragCollect)(SearchPinsRow);
